/**
 * "New client": the requests zCRM sends to create an account in zInspector.
 *
 * An account is a property in a "CRM – <Market>" user group, its details are zData rows in the
 * "Account" area, and its people are contacts of type Tenant linked to it (handoff: "Decisions
 * made"). zInspector's public API does not accept these writes yet: the routes below are the ones
 * zCRM proposes (zcrm-docs/reference/proposed-api-writes.md). Against the live API the screen only
 * previews these requests; the demo's fake zInspector runs them.
 */
import {
  ACCOUNT_DETAILS as ALL_ACCOUNT_DETAILS,
  ACCOUNT_KINDS,
  type AccountDetailKey,
} from "./crmConfig";

/** The form's values, one key per input. */
export interface NewClientInput {
  name: string;
  user_group: string;
  street_address?: string;
  street_address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  accounting_id?: string;
  account_kind?: string;
  website?: string;
  office_phone?: string;
  lead_source?: string;
  serviceable_doors?: string;
  expected_monthly_orders?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_email?: string;
  contact_mobile_phone?: string;
  contact_office_phone?: string;
  contact_role?: string;
  contact_marketing_consent?: boolean;
}

export interface ZiWriteRequest {
  method: "POST";
  path: string;
  body: Record<string, unknown>;
}

/** Stands in for the new property's id in the preview, before it exists. */
export const NEW_PROPERTY_ID = "<id returned by the first request>";

export { ACCOUNT_KINDS };

/** The form keys that are account details; their zData detail names come from crmConfig.ts. */
const FORM_DETAIL_KEYS = [
  "account_kind",
  "website",
  "office_phone",
  "lead_source",
  "serviceable_doors",
  "expected_monthly_orders",
] as const satisfies readonly (AccountDetailKey & keyof NewClientInput)[];

/** Account details on the form: form key → zData detail name in the "Account" area. */
export const ACCOUNT_DETAILS = FORM_DETAIL_KEYS.map((key) => ({
  key,
  detail: ALL_ACCOUNT_DETAILS.find((d) => d.key === key)?.detail ?? key,
}));

const clean = (value: string | undefined): string => (value ?? "").trim();

/** Drops empty values so the requests only carry what the user typed. */
const compact = (fields: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== "" && value != null),
  );

export const hasContact = (input: NewClientInput): boolean =>
  [
    input.contact_first_name,
    input.contact_last_name,
    input.contact_email,
    input.contact_mobile_phone,
    input.contact_office_phone,
  ].some((value) => clean(value) !== "");

/**
 * The requests, in order: the property first, then (with its id) the account details and the
 * main contact. Sections the user left empty produce no request.
 */
export const buildNewClientRequests = (
  input: NewClientInput,
  propertyId: number | string = NEW_PROPERTY_ID,
): ZiWriteRequest[] => {
  const requests: ZiWriteRequest[] = [
    {
      method: "POST",
      path: "/property/create/",
      body: compact({
        Name: clean(input.name),
        UserGroup: input.user_group,
        StreetAddress: clean(input.street_address),
        StreetAddress2: clean(input.street_address2),
        City: clean(input.city),
        State: clean(input.state),
        Zip: clean(input.zip),
        Country: clean(input.country),
        AccountingID: clean(input.accounting_id),
      }),
    },
  ];

  const details = ACCOUNT_DETAILS.map(({ key, detail }) => ({
    detail,
    value: clean(input[key]),
  })).filter((row) => row.value !== "");
  if (details.length) {
    requests.push({
      method: "POST",
      path: "/zdata/create/",
      body: { Property: propertyId, area_name: "Account", values: details },
    });
  }

  if (hasContact(input)) {
    requests.push({
      method: "POST",
      path: "/contact/create/",
      body: compact({
        Property: propertyId,
        Type: "TENANT",
        FirstName: clean(input.contact_first_name),
        LastName: clean(input.contact_last_name),
        Company: clean(input.name),
        Email: clean(input.contact_email),
        MobilePhone: clean(input.contact_mobile_phone),
        OfficePhone: clean(input.contact_office_phone),
        TaxId: clean(input.contact_role),
        Is1099Required: !!input.contact_marketing_consent,
      }),
    });
  }
  return requests;
};
